/*
 * Voyado card video — lazy muted-autoplay for product-video slides.
 * -----------------------------------------------------------------
 * The Elevate feed carries a product video as an imageInfo entry (custom.type
 * "Video"); voyado-card-renderer.js renders it as a <video data-card-video>
 * slide with its mp4 in data-video-src (not src) and preload="none", so nothing
 * downloads up front. Here we observe each such <video> and, only while its
 * slide is actually on-screen, set the src + play it (muted loop); when it
 * scrolls away — or its gallery slide is swapped out (the off slide is
 * translate-x-full, clipped by the gallery's overflow-hidden → ratio 0) — we
 * pause. One shared IntersectionObserver drives every card. Matches the
 * voyado-price-storefront.js pattern: re-runs on each `voyado:cards-rendered`
 * and claims each element once so the PLP's whole-grid re-render (pagination)
 * doesn't re-observe already-wired videos.
 *
 * Reduced-motion: honoured — the poster stays and nothing autoplays.
 */
(function () {
  'use strict';

  if (!('IntersectionObserver' in window)) return;
  const reduce = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  if (reduce) return; // keep the poster still; never autoplay

  // Discrete play/pause control (snippets/card-video-toggle.liquid; mirrors the D73 banners): the toggle
  // stays hidden while the video plays and only appears (as a Play button) when the state flips to "paused"
  // — which we set ONLY when autoplay is blocked (a device limitation). Keeps aria in sync with playback.
  function toggleFor(vid) {
    // Card: the toggle is a sibling of the video's <a> inside [data-card-image]. PDP gallery: the video
    // and toggle are direct children of the slide/cell, so fall back to the video's own parent. Either
    // way we resolve the [data-video-toggle] that belongs to this video.
    const cell = vid.closest('[data-card-image]') || vid.parentElement;
    return cell && cell.querySelector('[data-video-toggle]');
  }
  function setState(vid) {
    const btn = toggleFor(vid);
    if (!btn) return;
    const paused = vid.paused;
    btn.setAttribute('data-video-state', paused ? 'paused' : 'playing');
    btn.setAttribute('aria-label', paused ? 'Play video' : 'Pause video');
    btn.setAttribute('aria-pressed', String(!paused));
  }

  function start(vid) {
    if (!vid.src) {
      const src = vid.getAttribute('data-video-src');
      if (!src) return;
      vid.src = src;
    }
    vid.muted = true; // property (not just attribute) — required for muted autoplay
    const p = vid.play();
    if (p && typeof p.catch === 'function') p.catch(function () { setState(vid); }); // blocked → reveal Play
  }

  // ≥60% of the video slide visible → it's the shown slide and in view.
  const io = new IntersectionObserver(function (entries) {
    entries.forEach(function (entry) {
      const vid = entry.target;
      if (entry.isIntersecting) start(vid);
      else if (!vid.paused) vid.pause();
    });
  }, { threshold: 0.6 });

  function wire(root) {
    const scope = (root && root.querySelectorAll) ? root : document;
    scope.querySelectorAll('[data-card-video]:not([data-video-init])').forEach(function (vid) {
      vid.setAttribute('data-video-init', '');
      vid.addEventListener('play', function () { setState(vid); });
      vid.addEventListener('pause', function () { setState(vid); });
      const btn = toggleFor(vid);
      if (btn) btn.addEventListener('click', function (e) {
        e.preventDefault();
        if (vid.paused) start(vid); else vid.pause();
      });
      io.observe(vid);
    });
  }

  document.addEventListener('voyado:cards-rendered', function (e) {
    wire(e.detail && e.detail.root);
  });
  wire(document); // any cards already in the DOM
})();
